export interface CampaignDetailAction {
  label: string;
  text: string;
}

export interface ParsedCampaignDetail {
  tagline: string;
  body: string;
  actions: CampaignDetailAction[];
  listIntro: string;
  signOff: string;
}

export function parseCampaignDetail(description: string): ParsedCampaignDetail {
  const raw = (description ?? '').trim();
  const normalized = raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\u00a0/g, ' ').trim();

  if (!normalized) {
    return { tagline: '', body: '', actions: [], listIntro: '', signOff: '' };
  }

  const lines = normalizeDescriptionLines(normalized);

  if (!lines.length) {
    return { tagline: '', body: '', actions: [], listIntro: '', signOff: '' };
  }

  const listStartIndex = lines.findIndex((line) => isBulletLine(line));
  const listIntro = deriveListIntro(lines, listStartIndex);
  const taglineIndex = lines.findIndex((line) => line.includes('🌟'));

  const tagline = taglineIndex >= 0 ? lines[taglineIndex] : '';

  let bodyLines: string[] = [];
  if (taglineIndex >= 0) {
    const endIndex = listStartIndex >= 0 ? listStartIndex : lines.length;
    bodyLines = lines.slice(taglineIndex + 1, endIndex);
  } else if (listStartIndex >= 0) {
    bodyLines = lines.slice(0, listStartIndex);
  } else {
    bodyLines = [...lines];
  }

  const signOffLine = lines.at(-1);
  const signOff = signOffLine && !isBulletLine(signOffLine) && !signOffLine.includes('🌟') ? signOffLine : '';

  if (signOff) {
    const lastBodyIndex = lines.lastIndexOf(signOff);
    if (lastBodyIndex > 0) {
      const candidateBody = lines.slice(taglineIndex >= 0 ? taglineIndex + 1 : 0, lastBodyIndex);
      if (candidateBody.length) {
        bodyLines = candidateBody.filter((line) => !isBulletLine(line));
      }
    }
  }

  const actionLines = lines.filter((line) => isBulletLine(line));
  const actions = actionLines.map((line) => {
    const trimmed = line.replace(/^-\s*/, '').trim();
    const separatorIndex = trimmed.indexOf(':');

    if (separatorIndex > 0) {
      const label = trimmed.slice(0, separatorIndex).trim();
      const text = trimmed.slice(separatorIndex + 1).trim();
      return { label, text };
    }

    return { label: '', text: trimmed };
  });

  const body = bodyLines
    .filter((line) => !isBulletLine(line) && line !== signOff && line !== listIntro)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    tagline,
    body,
    actions,
    listIntro,
    signOff,
  };
}

function normalizeDescriptionLines(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function isBulletLine(line: string): boolean {
  return line.trim().startsWith('- ');
}

function deriveListIntro(lines: string[], firstBulletIndex: number): string {
  if (firstBulletIndex <= 0) {
    return '';
  }

  const candidate = lines[firstBulletIndex - 1]?.trim() ?? '';
  if (!candidate) {
    return '';
  }

  const normalized = candidate.replace(/:$/, '').trim();
  if (!normalized || normalized.length > 120 || /[.!?]$/.test(normalized)) {
    return '';
  }

  return normalized;
}
